import { useCallback, useMemo } from 'react';
import type { IFilterButton } from '@/creditflowresearch/ui/common/components/organisms/ButtonGroup';
import { IActiveItem } from '@/creditflowresearch/ui/common/hooks/filters/useFilterButtons';
import { extractActiveItems } from '@/creditflowresearch/ui/common/hooks/filters/filterHelpers';

export interface IFilterItem {
  id: string,
  abbreviation: string,
  name: string,
}

interface ICreateFilterButtonsConfig<T extends IFilterItem> {
  hasAccessCheck?: (item: T) => boolean,
  getTooltip: (item: T) => string,
  getIsActive?: (item: T, selectedId: string) => boolean,
  shouldAllowClick?: (item: T) => boolean,
}

export interface IFilterButtonsResult {
  selectedFilterId: string,
  filterButtonItems: IFilterButton[],
  handleFilterClick: (item: IFilterButton) => void,
  getActiveItems: () => IActiveItem[],
}

export const useCreateFilterButtons = <T extends IFilterItem>(
  items: T[],
  selectedFilterId: string,
  onSelect: (id: string) => void,
  config: ICreateFilterButtonsConfig<T>,
): IFilterButtonsResult => {
  const {
    hasAccessCheck = () => true,
    getTooltip,
    getIsActive = (item, selected) => item.id === selected,
    shouldAllowClick = (item) => hasAccessCheck(item),
  } = config;

  const filterButtonItems: IFilterButton[] = useMemo(() => {
    if (!items || items.length === 0) {
      return [];
    }

    return items.map((item: T) => {
      const hasAccess = hasAccessCheck(item);
      const isActive = getIsActive(item, selectedFilterId);
      const tooltip = hasAccess ? getTooltip(item) : undefined;

      return {
        key: item.id,
        title: item.abbreviation,
        dataTest: 'togglingFilterApplyButton',
        isActive,
        hasAccess,
        tooltip,
        onClick: () => {
          if (shouldAllowClick(item)) {
            onSelect(item.id);
          }
        },
      };
    });
  }, [items, selectedFilterId, hasAccessCheck, getTooltip, getIsActive, shouldAllowClick, onSelect]);

  const handleFilterClick = useCallback((item: IFilterButton) => {
    if (!item.hasAccess) {
      return;
    }
    const selectedItem = items.find((i) => i.abbreviation === item.title);
    if (selectedItem) {
      onSelect(selectedItem.id);
    }
  }, [items, onSelect]);

  const getActiveItems = useCallback((): IActiveItem[] => {
    return extractActiveItems(filterButtonItems);
  }, [filterButtonItems]);

  return {
    selectedFilterId,
    filterButtonItems,
    handleFilterClick,
    getActiveItems,
  };
};
